import {
  cleanNumber,
  FeatureName,
  formatNumber,
  incrementNumber,
  parseFormattedNumber,
  useIsFeatureEnabled,
} from '@gripp/shared-logic';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Platform, StyleSheet, View } from 'react-native';
import { IconButton } from 'react-native-paper';
import { Colors } from '../../themes';
import { TextInput as CropCircleTextInput } from '../crop-circle/Components/TextInput/TextInput';
import { tokens } from '../crop-circle/tokens';
import { styledTextInputStyles } from './controlledTextInput';
import {
  MaskedNumberInputOld,
  MaskedNumberInputProps,
} from './maskedNumberInput-old';

// Re-export type for consumers
export type { MaskedNumberInputProps } from './maskedNumberInput-old';

// TODO: Crop Circle Redesign: Remove after feature is fully rolled out
export const MaskedNumberInput = (props: MaskedNumberInputProps) => {
  const isCropCircleRedesignEnabled = useIsFeatureEnabled(
    FeatureName.CropCircleRedesign
  );
  if (!isCropCircleRedesignEnabled) {
    return <MaskedNumberInputOld {...props} />;
  }
  return <MaskedNumberInputBase {...props} />;
};

const MaskedNumberInputBase = ({
  value,
  onChangeText,
  onBlur,
  onFocus: onFocusProp,
  decimalPlaces = 0,
  hideClear = false,
  ...rest
}: MaskedNumberInputProps) => {
  const isFocusedRef = useRef(false);
  const [rawText, setRawText] = useState(() =>
    value != null && value !== '' && value !== '0'
      ? formatNumber(value, decimalPlaces)
      : ''
  );

  useEffect(() => {
    if (isFocusedRef.current) return;
    if (value == null || value === '' || value === '0') {
      setRawText('');
    } else {
      setRawText(formatNumber(value, decimalPlaces));
    }
  }, [value, decimalPlaces]);

  const handleChangeText = useCallback(
    (text: string) => {
      const cleaned = cleanNumber(text, decimalPlaces, false);
      setRawText(cleaned);
      const parsed = parseFormattedNumber(cleaned);
      if (cleaned === '') {
        onChangeText('');
      } else {
        onChangeText(decimalPlaces === 0 ? Math.round(parsed) : parsed);
      }
    },
    [decimalPlaces, onChangeText]
  );

  const handleFocus = useCallback(() => {
    isFocusedRef.current = true;
    onFocusProp?.();
  }, [onFocusProp]);

  const handleBlur = useCallback(() => {
    isFocusedRef.current = false;
    const parsed = parseFormattedNumber(rawText);
    const formatted = formatNumber(parsed, decimalPlaces);
    setRawText(formatted);
    onBlur?.();
  }, [rawText, decimalPlaces, onBlur]);

  const placeholder = rest.placeholder
    ? rest.placeholder
    : decimalPlaces > 0
    ? `0.${Array(decimalPlaces).fill('0').join('')}`
    : '0';

  // Same CropCircleTextInput for web and mobile so number input style matches
  return (
    <>
      {rest.showStepper && (
        <ShowStepperIconButton
          iconName="minus"
          onPress={() =>
            handleChangeText(
              `${incrementNumber(
                `${cleanNumber(rawText, decimalPlaces, true)}`,
                -1,
                decimalPlaces
              )}`
            )
          }
          isLeft={false}
          disabled={rest.disabled}
        />
      )}
      {rest.label ? (
        <CropCircleTextInput
          label={rest.label}
          value={rawText}
          onChangeText={handleChangeText}
          onFocus={handleFocus}
          onBlur={handleBlur}
          placeholder={placeholder}
          disabled={rest.disabled}
          keyboardType={Platform.OS === 'ios' ? 'decimal-pad' : 'numeric'}
          height={rest.height || 52}
          backgroundColor={tokens.colors.neutral[200]}
          hideBorder
        />
      ) : (
        <View style={styles.container}>
          <CropCircleTextInput
            value={rawText}
            onChangeText={handleChangeText}
            onFocus={handleFocus}
            onBlur={handleBlur}
            placeholder={placeholder}
            disabled={rest.disabled}
            keyboardType={Platform.OS === 'ios' ? 'decimal-pad' : 'numeric'}
            height={rest.height || 52}
            backgroundColor="transparent"
            hideBorder
            inputStyle={styles.inputNoPadding}
            clearButtonStyle={styles.clearButtonCentered}
            rightIconStyle={styles.rightIconCentered}
          />
        </View>
      )}
      {rest.showStepper && (
        <ShowStepperIconButton
          iconName="plus"
          onPress={() =>
            handleChangeText(
              `${incrementNumber(
                `${cleanNumber(rawText, decimalPlaces, true)}`,
                +1,
                decimalPlaces
              )}`
            )
          }
          isLeft
          disabled={rest.disabled}
        />
      )}
    </>
  );
};

const styles = StyleSheet.create({
  container: {
    borderRadius: 4,
    backgroundColor: tokens.colors.neutral[200],
    borderWidth: 0,
    minHeight: 52,
    paddingHorizontal: tokens.spacing.lg,
    justifyContent: 'center',
    overflow: 'hidden',
    flex: 4,
  },
  inputNoPadding: {
    paddingTop: 0,
    paddingBottom: 0,
    paddingLeft: 0,
    // Keep right padding for icon positioning - PaperTextInput.Icon uses contentStyle paddingRight
    // Container has paddingHorizontal: 16px, so we want icon at TextInput's right edge (16px from container)
    paddingRight: 0,
    textAlignVertical: 'center',
    // Ensure vertical centering for both PaperTextInput and RNTextInput
    ...(Platform.OS !== 'web' && {
      includeFontPadding: false,
    }),
  },
  clearButtonCentered: {
    // Center vertically: (52px container - 20px button) / 2 = 16px from top
    top: 16,
  },
  rightIconCentered: {
    // Override contentSingleLine padding to center the icon vertically
    paddingTop: 0,
    paddingBottom: 0,
    paddingRight: 0,
    // Center the icon - PaperTextInput.Icon style accepts TextStyle
    textAlignVertical: 'center',
  },
});

const ShowStepperIconButton = (props: {
  iconName: string;
  onPress: () => void;
  isLeft: boolean;
  disabled?: boolean;
}) => (
  <IconButton
    mode="outlined"
    icon={props.iconName}
    iconColor={Colors.grayText}
    size={24}
    style={[
      styledTextInputStyles.counterButton,
      props.isLeft
        ? { borderLeftWidth: 1, borderLeftColor: Colors.grayText }
        : { borderRightWidth: 1, borderRightColor: Colors.grayText },
    ]}
    onPress={props.onPress}
    disabled={props.disabled}
  />
);
